/**
 * Application shell: model editing on the left, results on the right.
 *
 * Analysis runs in Workers so the UI thread stays free (PRD §6.1). The seed is
 * a first-class, visible field rather than a hidden implementation detail —
 * a report nobody can reproduce is not an engineering document (PRD §4.8).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Project } from '../engine/model.ts';
import { buildModel } from '../engine/model.ts';
import { analyzeWorstCase } from '../engine/wca.ts';
import { analyzeRss } from '../engine/rss.ts';
import {
  ENGINE_VERSION,
  compareOutput,
  type FullAnalysis,
  type OutputComparison,
} from '../engine/analysis.ts';
import { McPool, defaultWorkerCount, type RunProgress } from '../worker/pool.ts';
import { parseProjectFile, serializeProject, SchemaError } from '../storage/schema.ts';
import {
  newProjectId,
  saveProject,
  listProjects,
  loadProject,
  deleteProject,
  storageAvailable,
  type ProjectRecord,
} from '../storage/db.ts';
import { ModelPanel } from './panels/ModelPanel.tsx';
import { ResultsPanel } from './panels/ResultsPanel.tsx';
import { ModelSummary } from './panels/ModelSummary.tsx';
import { Guide } from './Guide.tsx';
import { Info } from './Info.tsx';
import { GuideContext, type GuideApi } from './guide-context.ts';
import type { TopicId } from './glossary.tsx';
import { EXAMPLES, emptyProject } from './examples.ts';
import { count, duration, percent } from './format.ts';

const ITERATION_CHOICES = [10_000, 100_000, 1_000_000, 10_000_000];

type Status =
  | { kind: 'idle' }
  | { kind: 'running'; progress: RunProgress }
  | { kind: 'error'; message: string };

export function App() {
  const [project, setProject] = useState<Project>(() => EXAMPLES[0].build());
  const [projectId, setProjectId] = useState<string>(() => newProjectId());
  const [iterations, setIterations] = useState(100_000);
  const [analysis, setAnalysis] = useState<FullAnalysis | null>(null);
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const [selectedOutput, setSelectedOutput] = useState(0);
  const [saved, setSaved] = useState<ProjectRecord[]>([]);
  const [showLibrary, setShowLibrary] = useState(false);
  const [guideTopic, setGuideTopic] = useState<TopicId | null>(null);
  const [guideOpen, setGuideOpen] = useState(false);

  const poolRef = useRef<McPool | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  // The project as it was seeded or last opened, by identity. Autosave compares
  // against it so merely *looking* at an example never writes a record: opening
  // the app used to mint a fresh id and save the untouched default 1.2 s later,
  // which left one duplicate in the library per visit. Every edit path replaces
  // the project object, so identity is a sufficient dirty check.
  const pristineRef = useRef<Project>(project);
  // Kept out of the record we write so reopening a project does not reset its
  // creation date to the moment of the latest keystroke.
  const createdAtRef = useRef<number>(Date.now());

  poolRef.current ??= new McPool();

  // Validate continuously so the Run button reflects whether the model is sound.
  const modelError = useMemo(() => {
    try {
      buildModel(project);
      return null;
    } catch (e) {
      return (e as Error).message;
    }
  }, [project]);

  // Stable, so the `?` markers scattered through the panels do not re-render
  // the whole model tree every time the shell updates.
  const guideApi = useMemo<GuideApi>(
    () => ({
      open: (topic) => {
        setGuideTopic(topic ?? null);
        setGuideOpen(true);
      },
    }),
    [],
  );

  const refreshLibrary = useCallback(() => {
    if (!storageAvailable()) return;
    listProjects().then(setSaved).catch(() => undefined);
  }, []);

  useEffect(refreshLibrary, [refreshLibrary]);

  // Autosave, debounced. A crash or accidental close must not lose work — but
  // only once there is work: an untouched example or a just-opened project is
  // not something the user asked to keep.
  useEffect(() => {
    if (!storageAvailable() || modelError) return;
    if (project === pristineRef.current) return;
    const t = setTimeout(() => {
      saveProject({
        id: projectId,
        name: project.name,
        project,
        createdAt: createdAtRef.current,
        updatedAt: Date.now(),
      })
        .then(refreshLibrary)
        .catch(() => undefined);
    }, 1200);
    return () => clearTimeout(t);
  }, [project, projectId, modelError, refreshLibrary]);

  useEffect(() => {
    setSelectedOutput((i) => Math.min(i, Math.max(0, project.outputs.length - 1)));
  }, [project.outputs.length]);

  useEffect(() => () => poolRef.current?.terminate(), []);

  const run = async () => {
    if (modelError) return;
    const controller = new AbortController();
    abortRef.current = controller;
    setStatus({ kind: 'running', progress: { completed: 0, total: iterations, fraction: 0 } });

    try {
      const started = Date.now();
      const monteCarlo = await poolRef.current!.run(project, {
        iterations,
        seed: project.seed,
        signal: controller.signal,
        onProgress: (progress) => setStatus({ kind: 'running', progress }),
      });

      // WCA and RSS are milliseconds of work on the main thread; shipping them
      // to a worker would cost more in transfer than it saves.
      const model = buildModel(project);
      const comparisons: OutputComparison[] = model.outputs.map((out, i) =>
        compareOutput(out.spec, analyzeWorstCase(model, i, { seed: project.seed }),
          analyzeRss(model, i), monteCarlo.outputs[i]),
      );

      setAnalysis({
        monteCarlo,
        comparisons,
        seed: project.seed,
        provenance: {
          project: project.name,
          seed: project.seed,
          iterations: monteCarlo.completedIterations,
          engineVersion: ENGINE_VERSION,
          timestamp: new Date().toISOString(),
        },
      });
      setStatus({ kind: 'idle' });
      void started;
    } catch (e) {
      setStatus({ kind: 'error', message: (e as Error).message });
    } finally {
      abortRef.current = null;
    }
  };

  const cancel = () => abortRef.current?.abort();

  /**
   * Adopt a project the user did not type: a seeded example, an import, or a
   * record reopened from the library. Marking it pristine is what stops the
   * autosave below from writing a copy of something the user only looked at.
   */
  const adopt = (next: Project, id: string, createdAt: number) => {
    pristineRef.current = next;
    createdAtRef.current = createdAt;
    setProject(next);
    setProjectId(id);
    setAnalysis(null);
  };

  const exportJson = () => {
    const blob = new Blob([serializeProject(project)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${project.name.replace(/[^\w.-]+/g, '_') || 'project'}.tola.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importJson = async (file: File) => {
    try {
      const text = await file.text();
      adopt(parseProjectFile(text), newProjectId(), Date.now());
      setStatus({ kind: 'idle' });
    } catch (e) {
      setStatus({
        kind: 'error',
        message:
          e instanceof SchemaError
            ? `Could not import this file — ${e.message}`
            : (e as Error).message,
      });
    }
  };

  const openExample = (index: number) => {
    adopt(EXAMPLES[index].build(), newProjectId(), Date.now());
    setShowLibrary(false);
  };

  return (
    <GuideContext.Provider value={guideApi}>
      <div className="app">
        <header className="topbar">
          <div className="brand">
            <span className="logo">tola</span>
            <span className="tagline">statistical tolerance analysis</span>
            <span className="copyright">© 2026 <a href='https://www.willab.ch'> willab.ch </a> </span>
          </div>

          <input
            className="project-name"
            value={project.name}
            aria-label="Project name"
            onChange={(e) => setProject({ ...project, name: e.target.value })}
          />

          <div className="topbar-actions">
            <button type="button" onClick={() => setShowLibrary((v) => !v)}>
              Open
            </button>
            <button type="button" onClick={exportJson}>
              Export
            </button>
            <button type="button" onClick={() => fileRef.current?.click()}>
              Import
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".json,application/json"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void importJson(f);
                e.target.value = '';
              }}
            />
            <button type="button" onClick={() => window.print()} disabled={!analysis}>
              Report
            </button>
            <button type="button" onClick={() => guideApi.open()}>
              Guide
            </button>
          </div>
        </header>

        {guideOpen && (
          <Guide focus={guideTopic ?? undefined} onClose={() => setGuideOpen(false)} />
        )}

        {showLibrary && (
          <div className="library">
            <section>
              <h4>Examples</h4>
              <ul>
                {EXAMPLES.map((ex, i) => (
                  <li key={ex.id}>
                    <button type="button" className="library-item" onClick={() => openExample(i)}>
                      <strong>{ex.title}</strong>
                      <span>{ex.blurb}</span>
                    </button>
                  </li>
                ))}
                <li>
                  <button
                    type="button"
                    className="library-item"
                    onClick={() => {
                      adopt(emptyProject(), newProjectId(), Date.now());
                      setShowLibrary(false);
                    }}
                  >
                    <strong>Empty project</strong>
                    <span>Start from nothing.</span>
                  </button>
                </li>
              </ul>
            </section>

            {saved.length > 0 && (
              <section>
                <div className="library-heading">
                  <h4>Saved on this device</h4>
                  <button
                    type="button"
                    className="link-btn"
                    onClick={() => {
                      if (!confirm(`Delete all ${saved.length} saved projects? This cannot be undone.`))
                        return;
                      void Promise.all(saved.map((r) => deleteProject(r.id))).then(refreshLibrary);
                    }}
                  >
                    Delete all
                  </button>
                </div>
                <ul>
                  {saved.map((rec) => (
                    <li key={rec.id}>
                      <button
                        type="button"
                        className="library-item"
                        onClick={() => {
                          void loadProject(rec.id).then((r) => {
                            if (!r) return;
                            adopt(r.project, r.id, r.createdAt);
                            setShowLibrary(false);
                          });
                        }}
                      >
                        <strong>{rec.name}</strong>
                        <span>{new Date(rec.updatedAt).toLocaleString()}</span>
                      </button>
                      <button
                        type="button"
                        className="danger-btn"
                        aria-label={`Delete ${rec.name}`}
                        onClick={() => void deleteProject(rec.id).then(refreshLibrary)}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}

        <main className="layout">
          <aside className="panel model-side">
            <ModelPanel project={project} onChange={setProject} />
          </aside>

          <section className="panel results-side">
            <div className="runbar">
              <label className="field narrow">
                <span>
                  Iterations <Info topic="iterations" />
                </span>
                <select
                  value={iterations}
                  onChange={(e) => setIterations(Number(e.target.value))}
                >
                  {ITERATION_CHOICES.map((n) => (
                    <option key={n} value={n}>
                      {count(n)}
                    </option>
                  ))}
                </select>
              </label>

              <label className="field narrow">
                <span>
                  Seed <Info topic="seed" />
                </span>
                <input
                  type="number"
                  value={project.seed}
                  min={0}
                  max={4294967295}
                  onChange={(e) =>
                    setProject({ ...project, seed: Math.max(0, Math.floor(Number(e.target.value))) })
                  }
                />
              </label>

              <button
                type="button"
                className="reseed"
                onClick={() =>
                  setProject({ ...project, seed: Math.floor(Math.random() * 0xffffffff) })
                }
                title="New random seed"
              >
                ↻
              </button>

              {status.kind === 'running' ? (
                <button type="button" className="primary" onClick={cancel}>
                  Cancel ({percent(status.progress.fraction, 0)})
                </button>
              ) : (
                <button
                  type="button"
                  className="primary"
                  onClick={() => void run()}
                  disabled={!!modelError}
                >
                  Run analysis
                </button>
              )}

              <span className="worker-note">
                {defaultWorkerCount()} worker{defaultWorkerCount() === 1 ? '' : 's'}
              </span>
            </div>

            {status.kind === 'running' && (
              <div className="progress" role="progressbar" aria-valuenow={Math.round(status.progress.fraction * 100)} aria-valuemin={0} aria-valuemax={100}>
                <div className="progress-bar" style={{ width: `${status.progress.fraction * 100}%` }} />
              </div>
            )}

            {modelError && <p className="callout bad">{modelError}</p>}
            {status.kind === 'error' && <p className="callout bad">{status.message}</p>}

            {!analysis && !modelError && (
              <div className="empty-state">
                <h2>Ready to run</h2>
                <p>
                  Define variables and a transfer function on the left, then run. Results are
                  computed entirely in this browser — nothing is uploaded.
                </p>
                <p>
                  New to tolerance analysis, or to this tool?{' '}
                  <button type="button" className="link-btn" onClick={() => guideApi.open()}>
                    Read the guide
                  </button>{' '}
                  — it explains every field and what goes wrong if you get it wrong. Or open an
                  example from the Open menu and run it.
                </p>
              </div>
            )}

            {analysis && (
              <>
                {analysis.monteCarlo.systemYield && project.outputs.length > 1 && (
                  <div className="system-yield">
                    <strong>System yield</strong> {percent(analysis.monteCarlo.systemYield.point, 4)}
                    <span className="muted">
                      {' '}
                      — all outputs simultaneously in spec, computed per sample (not the product of
                      the individual yields)
                    </span>
                  </div>
                )}

                {project.outputs.length > 1 && (
                  <nav className="tabs">
                    {analysis.monteCarlo.outputs.map((o, i) => (
                      <button
                        key={o.id}
                        type="button"
                        className={i === selectedOutput ? 'tab active' : 'tab'}
                        onClick={() => setSelectedOutput(i)}
                      >
                        {o.id}
                      </button>
                    ))}
                  </nav>
                )}

                <ResultsPanel
                  analysis={analysis}
                  outputIndex={selectedOutput}
                  project={project}
                />

                {/* Print only — the model column is hidden on paper, so without
                  this a report carries results with no inputs. */}
                <ModelSummary project={project} />

                <footer className="provenance">
                  Seed {analysis.provenance.seed} · {count(analysis.provenance.iterations)} iterations
                  · engine {analysis.provenance.engineVersion} ·{' '}
                  {duration(analysis.monteCarlo.elapsedMs)} ·{' '}
                  {new Date(analysis.provenance.timestamp).toLocaleString()} · tola © 2026
                  willab.ch
                </footer>
              </>
            )}
          </section>
        </main>
      </div>
    </GuideContext.Provider>
  );
}
